import React, { useState } from "react";
import "./Register.css";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { registerUser } from "../../../Store/userSlice";
import logo from "../../../assets/images/logo.png";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^\d{8}$/;
const passwordPattern = /^(?=.*[a-zA-Z])(?=.*\d).{6,}$/;
const namePattern = /^[A-Za-zÀ-ÿ\s]+$/;

const Register = () => {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const clearError = () => setError("");

  const validate = () => {
    if (!firstname.trim()) return "Le prénom est obligatoire.";
    if (!namePattern.test(firstname)) return "Le prénom doit contenir uniquement des lettres.";
    if (!lastname.trim()) return "Le nom de famille est obligatoire.";
    if (!namePattern.test(lastname)) return "Le nom de famille doit contenir uniquement des lettres.";
    if (!emailPattern.test(email)) return "Adresse e-mail invalide.";
    if (!phonePattern.test(phone)) return "Le numéro de téléphone doit comporter 8 chiffres.";
    if (!passwordPattern.test(password))
      return "Le mot de passe doit comporter au moins 6 caractères avec une lettre et un chiffre.";
    if (confirmPassword !== password)
      return "Les mots de passe ne correspondent pas.";
    if (!gender) return "Veuillez sélectionner votre genre.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const result = await dispatch(
        registerUser({ firstname, lastname, email, password, phone, gender })
      );

      if (result.payload?.success === false) {
        const msg = result.payload?.data?.message;
        if (Array.isArray(msg)) {
          setError(msg[0]);
        } else {
          setError(msg || "Une erreur est survenue. Veuillez réessayer.");
        }
      } else {
        navigate(`/verify-email-sent?email=${encodeURIComponent(email)}`);
      }
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="register-page">
        <div className="register-card">

          {/* Logo */}
          <NavLink to="/">
            <img src={logo} alt="Casa del Padel" className="register-logo" />
          </NavLink>

          <h1 className="register-title">Créer un compte</h1>
          <p className="register-subtitle">Rejoignez la communauté Casa del Padel</p>

          {/* Inline error */}
          {error && <div className="register-error">{error}</div>}

          <form className="register-form" onSubmit={handleSubmit} noValidate>

            {/* Name row */}
            <div className="register-name-row">
              <div className="register-field">
                <label className="register-label" htmlFor="reg-firstname">Prénom</label>
                <input
                  id="reg-firstname"
                  type="text"
                  className="register-input"
                  placeholder="Prénom"
                  value={firstname}
                  onChange={(e) => { setFirstname(e.target.value); clearError(); }}
                  autoComplete="given-name"
                />
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="reg-lastname">Nom</label>
                <input
                  id="reg-lastname"
                  type="text"
                  className="register-input"
                  placeholder="Nom"
                  value={lastname}
                  onChange={(e) => { setLastname(e.target.value); clearError(); }}
                  autoComplete="family-name"
                />
              </div>
            </div>

            {/* Email */}
            <div className="register-field">
              <label className="register-label" htmlFor="reg-email">Adresse e-mail</label>
              <input
                id="reg-email"
                type="email"
                className="register-input"
                placeholder="exemple@mail.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError(); }}
                autoComplete="email"
                autoCapitalize="none"
              />
            </div>

            {/* Phone */}
            <div className="register-field">
              <label className="register-label" htmlFor="reg-phone">Téléphone</label>
              <input
                id="reg-phone"
                type="tel"
                className="register-input"
                placeholder="8 chiffres"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); clearError(); }}
                autoComplete="tel"
                maxLength={8}
              />
            </div>

            {/* Password */}
            <div className="register-field">
              <label className="register-label" htmlFor="reg-password">Mot de passe</label>
              <div className="register-input-wrap">
                <input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  className="register-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError(); }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="register-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Masquer" : "Afficher"}
                >
                  {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
              </div>
              <span className="register-hint">Au moins 6 caractères avec une lettre et un chiffre.</span>
            </div>

            {/* Confirm password */}
            <div className="register-field">
              <label className="register-label" htmlFor="reg-confirm-password">
                Confirmer le mot de passe
              </label>
              <div className="register-input-wrap">
                <input
                  id="reg-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  className="register-input"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); clearError(); }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="register-eye-btn"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  aria-label={showConfirmPassword ? "Masquer" : "Afficher"}
                >
                  {showConfirmPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                </button>
              </div>
            </div>

            {/* Gender */}
            <div className="register-field">
              <label className="register-label">Genre</label>
              <div className="register-gender-row">
                <label className={`register-gender-option ${gender === "h" ? "register-gender-option--active" : ""}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="h"
                    checked={gender === "h"}
                    onChange={() => { setGender("h"); clearError(); }}
                  />
                  Homme
                </label>
                <label className={`register-gender-option ${gender === "f" ? "register-gender-option--active" : ""}`}>
                  <input
                    type="radio"
                    name="gender"
                    value="f"
                    checked={gender === "f"}
                    onChange={() => { setGender("f"); clearError(); }}
                  />
                  Femme
                </label>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" className="register-submit" disabled={loading}>
              {loading ? "Inscription en cours…" : "S'inscrire"}
            </button>
          </form>

          {/* Login link */}
          <p className="register-login-hint">
            Déjà inscrit ?{" "}
            <NavLink to="/login" className="register-login-link">
              Se connecter
            </NavLink>
          </p>

        </div>
      </div>
    </>
  );
};

export default Register;
